import React, { useEffect, useState } from 'react';
import './overview.css';
import { AiFillLock } from "react-icons/ai";
import ProjectDisplay from '../projectdisplay/ProjectDisplay';
import BigListing from '../biglisting/BigListing';
import VerifiedBox from '../verified-box/VerifiedBox';
import Marquee from 'react-fast-marquee';
import AdBar from '../adbar/AdBar';
import { Helmet } from 'react-helmet-async';

const Lock = AiFillLock as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const ListingOverview: React.FC = () => {
  const [initialData, setInitialData] = useState<any[]>([]);
  const [listingData, setListingData] = useState<any[]>([]);
  const [currentTime, setCurrentTime] = useState<any>(new Date());
  const [verifiedProjects, setVerified] = useState<any[]>([]);
  const [recent, setRecent] = useState<any[]>([]);
  const [upcoming, setUpcoming] = useState<any[]>([]);
  const [audits, setAudits] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [promotedJson, setPromoted] = useState<any>({});
  const [itemsToShow, setItemsToShow] = useState(30);

  useEffect(() => {
    const fetchPromoted = async () => {
      try {
        const res = await fetch(
          'https://coinsult.dev/wp-json/custom/v1/promoted-options/'
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const promoted = await res.json();
        setPromoted(promoted);
      } catch (err: any) {
        console.error('Failed to load promoted options:', err.message);
      }
    };

    fetchPromoted();
  }, []);


  // Fetch initial data for featured sections (fixed amount)
  const fetchInitialData = async () => {
    try {
      const res = await fetch(
        'https://coinsult.dev/wp-json/wp/v2/tokens?per_page=5&_fields=meta,title,date'
      );
      if (!res.ok) throw new Error('Network response was not ok');
      const tokens = await res.json();
      setRecent(tokens);
    } catch (err: any) {
      console.error(err.message);
    }

    try {
      const res = await fetch(
        'https://coinsult.dev/wp-json/wp/v2/tokens?recent_audits&per_page=5&_fields=meta,title,date'
      );
      if (!res.ok) throw new Error('Network response was not ok');
      const tokens = await res.json();
      setAudits(tokens);
    } catch (err: any) {
      console.error(err.message);
    }

    try {
      const res = await fetch(
        'https://coinsult.dev/wp-json/wp/v2/tokens?upcoming_events&per_page=5&_fields=meta,title,date'
      );
      if (!res.ok) throw new Error('Network response was not ok');
      const tokens = await res.json();
      setUpcoming(tokens);
    } catch (err: any) {
      console.error(err.message);
    }

    try {
      const res = await fetch(
        'https://coinsult.dev/wp-json/wp/v2/tokens?recent_verified&per_page=15&_fields=meta,title,date'
      );
      if (!res.ok) throw new Error('Network response was not ok');
      const tokens = await res.json();
      setVerified(tokens);
    } catch (err: any) {
      console.error(err.message);
    }
  };

  const fetchListingData = async (page: number) => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `https://coinsult.dev/wp-json/wp/v2/tokens?per_page=30&page=${page}&_fields=meta,title,date`
      );
      if (!res.ok) throw new Error('Network response was not ok');
      const tokens = await res.json();
      
      // If loading more pages, append to existing data
      if (page > 1) {
        setListingData(prevData => [...prevData, ...tokens]);
      } else {
        setListingData(tokens);
      }
    } catch (err: any) {
      console.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchListingData(nextPage);
    setItemsToShow(prev => prev + 30); // Increase items to show by 15
  };

  useEffect(() => {
    fetchInitialData();
    fetchListingData(1);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);


  const calculateCountdown = (launchStr: any) => {
    launchStr = launchStr + "Z";
    const launchTime = new Date(launchStr).getTime() / 1000;  // seconds
    const nowSeconds = currentTime.getTime() / 1000;           // seconds
    const secondsLeft = Math.floor(launchTime - nowSeconds);

    if (secondsLeft <= 0) return "Launched";
    if (secondsLeft >= 86400) {
      const days = Math.floor(secondsLeft / 86400);
      return `in ${days}d`;
    } else if (secondsLeft >= 3600) {
      const hours = Math.floor(secondsLeft / 3600);
      return `in ${hours}h`;
    } else if (secondsLeft >= 60) {
      const minutes = Math.floor(secondsLeft / 60);
      return `in ${minutes}m`;
    } else {
      return `in ${secondsLeft}s`;
    }
  };

  const relativeTime = (dateString: any) => {
    dateString = new Date(dateString + "Z");
    const diffSeconds = Math.floor((new Date().getTime() - dateString.getTime()) / 1000);
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 365) return `${diffDays}d ago`;
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears}y ago`;
  };

  const pinned_recent = promotedJson?.recent_listed_pinned?.["item-0"];
  const pinned_events = promotedJson?.upcoming_event_pinned?.["item-0"];
  const pinned_audits = promotedJson?.audit_completion_pinned?.["item-0"];

  // Extract all verified_pinned items
  const verifiedPinnedItems = Object.keys(promotedJson?.verified_pinned || {})
    .filter(key => key.startsWith('item-'))
    .map(key => promotedJson.verified_pinned[key]);

  const allprojectsPinned = Object.keys(promotedJson?.all_projects_pinned || {})
    .filter(key => key.startsWith('item-'))
    .map(key => promotedJson.all_projects_pinned[key]);

  return (
    <>
    <Helmet>
      <title>Listing Overview | Coinsult</title>
      <meta name="description" content="View realtime security insights price charts, trading history and helpfull info." />
      <link rel="canonical" href={`https://coinsult.net/overview`} />

      <meta property="og:type"        content="website" />
      <meta property="og:title"       content="Listing Overview | Coinsult" />
      <meta property="og:description" content="View realtime security insights price charts, trading history and helpfull info." />
      <meta property="og:image" content="/favicon.png" />

      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content="Listing Overview | Coinsult" />
      <meta name="twitter:description" content="View realtime security insights price charts, trading history and helpfull info." />
      <meta name="twitter:image" content="/favicon.png" />
    </Helmet>
        <AdBar />
      <div className="listing-main">
        <div className="fl-hor">
          <div className="fl-item">
            <h3 className='listing-title'>Recent Listings <a className="listnowbutton" href='/list'>List now</a></h3>
            {recent.length === 0 && (
              <div className="loading-spinner" />
            )}

            {pinned_recent && (
                <ProjectDisplay 
                  url={pinned_recent.url}
                  verified={pinned_recent.verified}
                  image={pinned_recent.image}
                  chain={pinned_recent.chain}
                  title={pinned_recent.project_name}
                  symbol={pinned_recent.project_ticker}
                  address={pinned_recent.contract_address}
                  banner={pinned_recent.banner}
                  square={"Round"}
                  isad={true}
                  change={true}
                />
            )}

            {recent.map((token, index) => {
              const { meta, date } = token;
              const tokenTitle = token.title?.rendered;
              return (
                <ProjectDisplay 
                  key={index}
                  verified={meta.verified}
                  image={meta.logo_file}
                  chain={meta.chain}
                  title={tokenTitle}
                  symbol={meta.symbol}
                  countdown={relativeTime(date)}
                  address={meta.contract_address}
                  banner={meta.banner}
                  square={meta.logo_format}
                  change={true}
                />
              );
            })}
          </div>
          {/* <div className="fl-item eventsfl">
            <h3 className='listing-title'>Upcoming Events <a className="listnowbutton" href='/list'>List now</a></h3>
            {upcoming.length === 0 && (
              <div className="loading-spinner" />
            )}

            {pinned_events && (
                <ProjectDisplay 
                  url={pinned_events.url}
                  verified={pinned_events.verified2}
                  image={pinned_events.image}
                  chain={pinned_events.chain}
                  title={pinned_events.project_name}
                  symbol={pinned_events.project_ticker}
                  address={pinned_events.contract_address}
                  banner={pinned_events.banner}
                  countdown={calculateCountdown(pinned_events.event_date)}
                  square={"Round"}
                  isad={true}
                  change={true}
                />
            )}

            {upcoming.map((token, index) => {
              const { meta } = token;
              const tokenTitle = token.title?.rendered;
              return (
                <ProjectDisplay 
                  key={index}
                  verified={meta.verified}
                  image={meta.logo_file}
                  chain={meta.chain}
                  title={tokenTitle}
                  symbol={meta.symbol}
                  countdown={calculateCountdown(meta.launch_date_amp_timestamp)}
                  address={meta.contract_address}
                  banner={meta.banner}
                  change={true}
                />
              );
            })}
          </div> */}
          <div className="fl-item">
            <h3 className='listing-title'>Audit Completions <a className="listnowbutton" href='/list'>List now</a></h3>
            {audits.length === 0 && (
              <div className="loading-spinner" />
            )}

            {pinned_audits && (
                <ProjectDisplay 
                  url={pinned_audits.url}
                  verified={pinned_audits.verified3}
                  image={pinned_audits.image}
                  chain={pinned_audits.chain}
                  title={pinned_audits.project_name}
                  symbol={pinned_audits.project_ticker}
                  address={pinned_audits.contract_address}
                  banner={pinned_audits.banner}
                  square={"Round"}
                  isad={true}
                  change={true}
                />
            )}

            {audits.map((token, index) => {
              const { meta, date } = token;
              const tokenTitle = token.title?.rendered;
              return (
                <ProjectDisplay 
                  key={index}
                  verified={meta.verified}
                  image={meta.logo_file}
                  chain={meta.chain}
                  title={tokenTitle}
                  symbol={meta.symbol}
                  countdown={relativeTime(date)}
                  address={meta.contract_address}
                  banner={meta.banner}
                  change={true}
                />
              );
            })}
          </div>
          <div className="fl-item auditfl">
            <h3 className='listing-title'>Verified Projects <a className="listnowbutton" href='/list'>List now</a></h3>
            {verifiedProjects.length === 0 && (
              <div className="loading-spinner" />
            )}

            {verifiedProjects.slice(0, 5).map((token, index) => {
              const { meta, date } = token;
              const tokenTitle = token.title?.rendered;
              return (
                <ProjectDisplay 
                  key={index}
                  verified={meta.verified}
                  image={meta.logo_file}
                  chain={meta.chain}
                  title={tokenTitle}
                  symbol={meta.symbol}
                  countdown={relativeTime(date)}
                  address={meta.contract_address}
                  banner={meta.banner}
                  change={true}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="horizontal-verified">
        <div className="horizontal-verified__mask">
          <Marquee 
            pauseOnHover={true} 
            speed={40}
          >
          {verifiedPinnedItems.map((item, index) => (
            <VerifiedBox
              key={`pinned-verified-${index}`}
              url={item.url}
              verified={item.verified4}
              image={item.project_logo}
              chain={item.chain}
              title={item.project_title}
              symbol={item.project_ticker}
              address={item.contract_address}
              round="Round"
              isad={true}
            />
          ))}
            {[
              ...verifiedProjects,
              ...verifiedProjects
            ].map((token, index) => {
              const { meta, date } = token;
              const tokenTitle = token.title?.rendered;
              return (
                <VerifiedBox
                  key={index}
                  verified={meta.verified}
                  image={meta.logo_file}
                  chain={meta.chain}
                  title={tokenTitle}
                  symbol={meta.symbol}
                  countdown={relativeTime(date)}
                  address={meta.contract_address}
                  round={meta.logo_format}
                  banner={meta.banner}
                />
              );
            })}
          </Marquee>
        </div>
      </div>

      <div className="big-listing" style={{marginBottom: "-20px"}}>
            <h3 className='listing-title'>All projects</h3>
      </div>
      <div className="big-listing">
        {listingData.length === 0 && (
          <div className="loading-spinner" />
        )}
        {allprojectsPinned.map((item, index) => {
          return (
            <BigListing 
              url={item.url}
              key={`project-${index}`}
              verified={item.verified5}
              image={item.image}
              chain={item.chain}
              title={item.project_name}
              symbol={item.project_ticker}
              countdown={"AD"}
              address={item.contract_address}
              badges={
                {
                  "Audited": `${item.audited === 'yes' ? 'true' : 'false'}`,
                  "KYC": `${item.kyc === 'yes' ? 'true' : 'false'}`,
                  "SAFU": "false",
                  "Based": "false"
                }
              }
              banner={item.banner}
              isad={true}
            />
          );
        })}
        {listingData.slice(0, itemsToShow - allprojectsPinned.length).map((token, index) => {
          const { meta, date } = token;
          const tokenTitle = token.title?.rendered;
          return (
            <BigListing 
              key={`project-${index}`}
              verified={meta.verified}
              image={meta.logo_file}
              chain={meta.chain}
              title={tokenTitle}
              symbol={meta.symbol}
              countdown={relativeTime(date)}
              address={meta.contract_address}
              badges={meta.badges}
              round={meta.logo_format}
              banner={meta.banner}
            />
          );
        })}
      </div>

      <div className="load-more-button">
        <div 
          className={`loadmorebutton ${isLoading ? 'loading' : ''}`} 
          onClick={handleLoadMore}
        >
          {isLoading ? 'Loading...' : 'Load more'}
        </div>
      </div>
    </>
  );
};

export default ListingOverview;